/**
 * @fileoverview Affiliate agreement edit dialog (MOR-438)
 *
 * Create or edit a partner agreement: rate schedule (with a live timeline
 * preview), gross-margin adjustment mechanism, and notes.
 */

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Separator } from "@/ui/primitives/separator";
import { Textarea } from "@/ui/primitives/textarea";
import { extractErrorMessage } from "@/utils/errors";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import type { Agreement, RateSchedule } from "./billingFormat";
import { RateScheduleTimeline } from "./billingShared";

type AdjustmentKind = Agreement["grossMarginAdjustment"]["kind"];

interface RatePeriodDraft {
  fromMonth: string;
  toMonth: string;
  ratePercent: string;
}

/** Clause 3.1 of the Design Partner agreement: 25/25/10/10/10 over 5 years. */
const CONTRACT_DEFAULT_PERIODS: RatePeriodDraft[] = [
  { fromMonth: "0", toMonth: "12", ratePercent: "25" },
  { fromMonth: "12", toMonth: "24", ratePercent: "25" },
  { fromMonth: "24", toMonth: "36", ratePercent: "10" },
  { fromMonth: "36", toMonth: "48", ratePercent: "10" },
  { fromMonth: "48", toMonth: "60", ratePercent: "10" },
];

/** Drafts → schedule for the live preview; invalid rows are skipped. */
function draftsToSchedule(periods: RatePeriodDraft[]): RateSchedule {
  const schedule: RateSchedule = [];
  for (const p of periods) {
    const fromMonth = Number.parseInt(p.fromMonth, 10);
    const ratePercent = Number.parseFloat(p.ratePercent);
    if (!Number.isFinite(fromMonth) || !Number.isFinite(ratePercent)) continue;
    const toMonth = p.toMonth.trim()
      ? Number.parseInt(p.toMonth, 10)
      : undefined;
    schedule.push({ fromMonth, toMonth, ratePercent });
  }
  return schedule;
}

export function AgreementDialog({
  agreement,
  onClose,
}: {
  agreement: Agreement | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(agreement?.name ?? "");
  const [partnerEmail, setPartnerEmail] = useState(
    agreement?.partnerEmail ?? "",
  );
  const [notes, setNotes] = useState(agreement?.notes ?? "");
  const [archived, setArchived] = useState(agreement?.archived ?? false);
  const [adjustmentKind, setAdjustmentKind] = useState<AdjustmentKind>(
    agreement?.grossMarginAdjustment.kind ?? "margin_protection",
  );
  const [adjustmentPercent, setAdjustmentPercent] = useState(
    agreement?.grossMarginAdjustment.kind === "fixed_percent"
      ? String(agreement.grossMarginAdjustment.percent)
      : "",
  );
  const [fullMargin, setFullMargin] = useState(
    agreement?.grossMarginAdjustment.kind === "margin_protection"
      ? String(agreement.grossMarginAdjustment.fullMarginPercent)
      : "75",
  );
  const [floorMargin, setFloorMargin] = useState(
    agreement?.grossMarginAdjustment.kind === "margin_protection"
      ? String(agreement.grossMarginAdjustment.floorMarginPercent)
      : "50",
  );
  const [periods, setPeriods] = useState<RatePeriodDraft[]>(
    agreement
      ? agreement.rateSchedule.map((p) => ({
          fromMonth: String(p.fromMonth),
          toMonth: p.toMonth === undefined ? "" : String(p.toMonth),
          ratePercent: String(p.ratePercent),
        }))
      : CONTRACT_DEFAULT_PERIODS,
  );
  const [isSaving, setIsSaving] = useState(false);

  const saveAgreement = useMutation(api.admin.billing.saveAffiliateAgreement);

  const updatePeriod = (
    index: number,
    field: keyof RatePeriodDraft,
    value: string,
  ) => {
    setPeriods((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    );
  };

  const previewSchedule = draftsToSchedule(periods);

  const handleSave = async () => {
    const rateSchedule: Array<{
      fromMonth: number;
      toMonth?: number;
      ratePercent: number;
    }> = [];
    for (const p of periods) {
      const fromMonth = Number.parseInt(p.fromMonth, 10);
      const ratePercent = Number.parseFloat(p.ratePercent);
      if (!Number.isFinite(fromMonth) || !Number.isFinite(ratePercent)) {
        toast.error("Every rate period needs a start month and a rate.");
        return;
      }
      const toMonth = p.toMonth.trim()
        ? Number.parseInt(p.toMonth, 10)
        : undefined;
      rateSchedule.push({ fromMonth, toMonth, ratePercent });
    }
    setIsSaving(true);
    try {
      const grossMarginAdjustment =
        adjustmentKind === "fixed_percent"
          ? {
              kind: "fixed_percent" as const,
              percent: Number.parseFloat(adjustmentPercent) || 0,
            }
          : adjustmentKind === "margin_protection"
            ? {
                kind: "margin_protection" as const,
                fullMarginPercent: Number.parseFloat(fullMargin) || 75,
                floorMarginPercent: Number.parseFloat(floorMargin) || 50,
              }
            : { kind: adjustmentKind };
      await saveAgreement({
        agreementId: agreement?._id,
        name: name.trim(),
        partnerEmail: partnerEmail.trim() || undefined,
        notes: notes.trim() || undefined,
        rateSchedule,
        grossMarginAdjustment,
        archived,
      });
      toast.success(agreement ? "Agreement updated." : "Agreement created.");
      onClose();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save agreement."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] min-w-0 overflow-y-auto overflow-x-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {agreement ? "Edit affiliate agreement" : "New affiliate agreement"}
          </DialogTitle>
          <DialogDescription>
            Rate periods are measured in months since a firm&apos;s conversion
            to a paid plan (month 0 = first month). Leave the last period&apos;s
            end blank for an open-ended tail.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          {/* Partner ------------------------------------------------------- */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="agreement-name">Partner name</Label>
              <Input
                id="agreement-name"
                placeholder="Nicole Di Crescenzo"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="agreement-email">Partner email (optional)</Label>
              <Input
                id="agreement-email"
                type="email"
                value={partnerEmail}
                onChange={(e) => setPartnerEmail(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Rate schedule ------------------------------------------------- */}
          <div className="flex flex-col gap-3">
            <Label>Rate schedule</Label>
            <div className="grid grid-cols-[7rem_7rem_7rem_auto] items-center gap-2 text-xs text-muted-foreground">
              <span>From (month)</span>
              <span>To (month)</span>
              <span>Rate %</span>
              <span />
            </div>
            {periods.map((p, i) => (
              <div
                key={i}
                className="grid grid-cols-[7rem_7rem_7rem_auto] items-center gap-2"
              >
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={p.fromMonth}
                  onChange={(e) => updatePeriod(i, "fromMonth", e.target.value)}
                />
                <Input
                  type="number"
                  min="1"
                  placeholder="Open"
                  value={p.toMonth}
                  onChange={(e) => updatePeriod(i, "toMonth", e.target.value)}
                />
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="25"
                  value={p.ratePercent}
                  onChange={(e) =>
                    updatePeriod(i, "ratePercent", e.target.value)
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={periods.length === 1}
                  onClick={() =>
                    setPeriods((prev) => prev.filter((_, j) => j !== i))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPeriods((prev) => [
                    ...prev,
                    { fromMonth: "", toMonth: "", ratePercent: "" },
                  ])
                }
              >
                <Plus className="mr-1 h-4 w-4" />
                Add period
              </Button>
            </div>
            {previewSchedule.length > 0 && (
              <div className="flex flex-col gap-1.5 rounded-lg border bg-muted/30 p-3">
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Preview
                </span>
                <RateScheduleTimeline schedule={previewSchedule} />
              </div>
            )}
          </div>

          <Separator />

          {/* Adjustment ---------------------------------------------------- */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label>Gross-margin adjustment</Label>
              <Select
                value={adjustmentKind}
                onValueChange={(v) => setAdjustmentKind(v as AdjustmentKind)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="margin_protection">
                    Margin protection (contract clause 3.2)
                  </SelectItem>
                  <SelectItem value="none">None — fee on gross</SelectItem>
                  <SelectItem value="cost_of_service">
                    Cost of service — deduct vendor costs
                  </SelectItem>
                  <SelectItem value="fixed_percent">
                    Fixed percent of gross
                  </SelectItem>
                </SelectContent>
              </Select>
              {adjustmentKind === "margin_protection" && (
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Full share at/above the full-share margin; between the floor
                  and full-share margin the share is prorated (share × margin ÷
                  full); below the floor no share is payable.
                </p>
              )}
            </div>
            {adjustmentKind === "fixed_percent" && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="adjustment-percent">Adjustment %</Label>
                <Input
                  id="adjustment-percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={adjustmentPercent}
                  onChange={(e) => setAdjustmentPercent(e.target.value)}
                />
              </div>
            )}
            {adjustmentKind === "margin_protection" && (
              <div className="flex gap-3">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="full-margin">Full-share margin %</Label>
                  <Input
                    id="full-margin"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={fullMargin}
                    onChange={(e) => setFullMargin(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="floor-margin">Floor margin %</Label>
                  <Input
                    id="floor-margin"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={floorMargin}
                    onChange={(e) => setFloorMargin(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="agreement-notes">Notes</Label>
            <Textarea
              id="agreement-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            {agreement ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setArchived((a) => !a)}
              >
                {archived ? "Unarchive" : "Archive"}
              </Button>
            ) : (
              <span />
            )}
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save agreement"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
